import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { Community, UNKNOWN_COMMUNITY } from "@/types/community";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import CancelCreate from '@/components/CancelCreate';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: '100%',
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    width: '100%',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
});

export default function Details() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const descriptionInput = useRef<any>(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const userInfo = useSelector((state: RootState) => state.user.user_info);
  const { id } = useLocalSearchParams<{ id?: string }>();

  const { register, control, handleSubmit, setValue, getValues, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      description: '',
      link: '',
    }
  });

  const desc = watch("description");

  const loadInitialData = useCallback(async () => {
    try {
      showSpinner();
      
      const response = await AppUtilities.fetchAuthorized(
        "RabblAdmin.loadInitialData",
        "/rabbl/api/adminrabbl",
        "POST",
        {
          id: id,
        }
      );
      const communityResult: Community = ((response as any).body as any).community;

      setValue("link", communityResult.external_link ?? '');
      setValue("description", communityResult.details);

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => loadInitialData(), true);
      AppUtilities.handleException(e, "Details.loadInitialData");
    } finally {
      hideSpinner();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadInitialData();
    }
  }, [id]);

  async function onSubmit(data: any) {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "Details.onSubmit",
        "/rabbl/api/set_rabbl_details",
        "POST",
        {
          description: data.description,
          link: data.link,
          id: id,
        }
      );
      const creating_community: Community = ((response as any).body as any).creating_community;
      dispatch(setData(response));
      if (id && id.length > 0) {
        if (router.canGoBack()) {
          router.back();
        } else {
          router.navigate("/rabbls");
        }
      } else {
        router.navigate(AppUtils.getRabblCreateNextPageLink(creating_community, userInfo) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "Details.onSubmit");
    } finally {
      hideSpinner();
    }
  };

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (descriptionInput && descriptionInput.current) {
        descriptionInput.current?.focus();
      }
    }
  }, [descriptionInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Details"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          
          <AppText>This will be shown on the Rabbl homepage:</AppText>

          <Controller
            control={control}
            name="description"
            rules={{
              validate: (x) => {
                if (x == '') {
                  return 'Rabbl description is required.';
                }
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <TextInput
                  ref={descriptionInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Describe your community (keep it short)"
                  returnKeyType="next"
                  onBlur={onBlur}
                  multiline={true}
                  numberOfLines={4}
                  style={AppConstants.STYLES.textInputMultiline}
                />
              </View>
            )}
          />
          {errors.description && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.description.message} ^</AppText>}

          <Controller
            control={control}
            name="link"
            rules={{
              validate: (x) => {
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <TextInput
                  value={value}
                  onChangeText={onChange}
                  placeholder="External website (optional)"
                  style={styles.formElement}
                  returnKeyType="next"
                  onBlur={onBlur}
                  onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                />
              </View>
            )}
          />
          {errors.link && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.link.message} ^</AppText>}

          <View style={styles.buttonWrapper}>
            <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)} disabled={AppUtilities.isBlank(desc)}>Next</Button95>
          </View>
        </View>
      </Panel>
      
      { (id == null || id.length == 0) && <CancelCreate /> }
      
      <Footer />
    </BasicScrollView>
  );
}
